import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CloudOff, Loader2, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { currentAgentQueryKey, useCurrentAgent } from '@/features/auth/useCurrentAgent';
import { isHubOutageError } from '@/lib/api-client';
import { useRealtime } from '@/lib/realtime/useRealtime';
import { useAuthStore } from '@/store/auth';

/**
 * Protected route boundary.
 *
 * States, in the order they are checked:
 *   1. No token in the store -> immediate redirect to `/login`.
 *      `redirectTo` carries the URL the user wanted so LoginPage can return
 *      them there post-login.
 *   2. `/auth/me` REJECTED the session -> silent-clear + redirect to `/login`.
 *      The axios 401 interceptor handles the common case; this defensive
 *      branch catches the other non-outage errors (a 403, a malformed 200).
 *   3. `/auth/me` ok -> render `<AuthedShell>` which mounts the realtime
 *      hook AND the children. The split keeps the realtime side-effect
 *      mounted only when we're sure the user is authenticated (the
 *      WebSocket would fail handshake otherwise) and ensures it lives
 *      INSIDE the QueryClientProvider so `useQueryClient` resolves.
 *   4. The HUB is out -> `HubOutageScreen` (bug 5; spec
 *      `docs/specs/2026-09-features/01-disconnect-early-warning.md` §3.6
 *      item 5). "Out" = `isHubOutageError`: unreachable (network / nginx's
 *      edge page / a proxy's 502·504) or the hub's own 5xx. That is not a
 *      verdict on the session, so the token is KEPT and `useCurrentAgent`
 *      keeps retrying with backoff; when the hub answers, the console opens
 *      by itself. (It used to clear the session on ANY non-401 error, so a
 *      20 s hub restart logged out every agent who reloaded.) While React
 *      Query retries, the query stays `pending` and the error is only
 *      visible as `failureReason` — hence both are consulted.
 *   5. Otherwise (`/auth/me` in flight) -> centered spinner (NOT a flash of
 *      unstyled content; the boundary owns the loading frame).
 */
export function RequireAuth({ children }: { children: ReactNode }): React.JSX.Element {
  const accessToken = useAuthStore((s) => s.accessToken);
  const clear = useAuthStore((s) => s.clear);
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isSuccess, isError, error, failureReason, failureCount, fetchStatus } =
    useCurrentAgent();
  // Set by the outage screen's retry button: from then on this boundary
  // keeps showing the outage screen (not the neutral spinner) until
  // `/auth/me` settles, even though the manual attempt starts from a reset
  // query with no `failureReason` yet.
  const [retryRequested, setRetryRequested] = useState(false);

  const sessionRejected = isError && !isHubOutageError(error);
  const hubOutage =
    !isSuccess &&
    !sessionRejected &&
    (retryRequested || isHubOutageError(error) || isHubOutageError(failureReason));

  // Defensive cleanup for non-401, non-outage errors — the axios interceptor
  // already handles 401. Keeping this in an effect (not inside the render
  // path) so we don't trigger state updates during render.
  useEffect(() => {
    if (sessionRejected) {
      clear();
    }
  }, [sessionRejected, clear]);

  if (!accessToken) {
    const redirectTo = `${location.pathname}${location.search}`;
    const params = redirectTo && redirectTo !== '/' ? `?redirectTo=${encodeURIComponent(redirectTo)}` : '';
    return <Navigate to={`/login${params}`} replace />;
  }

  if (sessionRejected) {
    return <Navigate to="/login" replace />;
  }

  if (isSuccess) {
    return <AuthedShell>{children}</AuthedShell>;
  }

  if (hubOutage) {
    return (
      <HubOutageScreen
        // A reset attempt that has not failed yet (a failure bumps the count).
        retrying={retryRequested && failureCount === 0 && fetchStatus !== 'idle'}
        onRetry={() => {
          setRetryRequested(true);
          // Try NOW instead of waiting out the backoff. `refetch()` cannot:
          // with no data yet it just rejoins the sleeping retry loop. A reset
          // cancels that loop and refetches the (active) query immediately;
          // the backoff restarts from 2 s. Its promise settles only when a
          // retry cycle ends — nothing to await here.
          void queryClient.resetQueries({ queryKey: currentAgentQueryKey, exact: true });
        }}
      />
    );
  }

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-background">
      <Loader2 className="size-8 animate-spin text-muted-foreground" aria-label="Loading" />
    </div>
  );
}

/**
 * Full-screen stand-in for the console while `/auth/me` cannot reach the
 * hub. Deliberately calm: the session is intact and the retry loop runs on
 * its own; the button only skips the current backoff wait.
 */
function HubOutageScreen({
  retrying,
  onRetry,
}: {
  retrying: boolean;
  onRetry: () => void;
}): React.JSX.Element {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-background p-6">
      <div
        role="alert"
        className="flex max-w-sm flex-col items-center gap-4 rounded-xl border border-border/40 bg-card p-8 text-center shadow-neu-raised-lg"
      >
        <CloudOff className="size-8 text-destructive" aria-hidden="true" />
        <p className="text-base font-semibold text-foreground">
          无法连接服务器，正在自动重试…（登录状态已保留）
        </p>
        <Button type="button" onClick={onRetry} disabled={retrying} aria-busy={retrying}>
          {retrying ? (
            <Loader2 className="animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCw aria-hidden="true" />
          )}
          立即重试
        </Button>
      </div>
    </div>
  );
}

/**
 * The portion of the tree that runs once `RequireAuth` has confirmed a valid
 * JWT. Owns the realtime WebSocket lifecycle via `useRealtime`. Kept as a
 * separate component so the hook mounts EXACTLY ONCE per authenticated
 * session — React 18's StrictMode double-invoke still triggers a single
 * mount+unmount+mount cycle, which the connection module dedupes via its
 * internal shutdown flag.
 */
function AuthedShell({ children }: { children: ReactNode }): React.JSX.Element {
  useRealtime();
  return <>{children}</>;
}
